import { ChangeEvent, useEffect, useState } from 'react';
import { isMobileDevice } from '@optimus-web/common';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { ListItem } from '../../../ListItem';
import { MobilePagination, Pagination } from '../../../Pagination';
import { Select } from '../../../Select';
import { Selected } from '../../../types';
import { useTableContext } from '../../TableContext';
import { tableStateReducer } from '../../tableStateReducer';
import { PAGINATION_SIZES, PaginationSize, ReducerActionType, TableFilters } from '../../types';
import { PaginationSizeSelectorWrapper, PaginationSizeWrapper, PaginationWrapper } from './style';

interface TablePaginationProps {
    size: number;
    totalCount: number;
}

const usePaginationComponent = () => {
    const [isMobile, setIsMobile] = useState(!!isMobileDevice()?.length);

    const handleResize = () => {
        setIsMobile(!!isMobileDevice()?.length);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return isMobile ? MobilePagination : Pagination;
};

const TablePagination = <T, F extends TableFilters<T>>({ size, totalCount }: TablePaginationProps) => {
    const { state, onStateChange } = useTableContext<T, F>();
    const PaginationComponent = usePaginationComponent();
    const { t } = useLocalization();

    const handlePageSizeChange = (_e: ChangeEvent, newPageSizeValue: Selected) => {
        const nextState = tableStateReducer(state, {
            type: ReducerActionType.Pagination,
            payload: {
                pageIndex: Math.floor(
                    (state.pagination.pageIndex * state.pagination.pageSize) / Number(newPageSizeValue)
                ),
                pageSize: newPageSizeValue as PaginationSize,
            },
        });
        onStateChange(nextState);
    };

    const handlePageChange = (page: number) => {
        const nextState = tableStateReducer(state, {
            type: ReducerActionType.Pagination,
            payload: { pageIndex: page },
        });
        onStateChange(nextState);
    };

    let PaginationSizeElement = null;

    if (totalCount && state.pagination.pageSize < totalCount) {
        PaginationSizeElement = (
            <>
                <span>{t(OptimusTextKey.TablePaginationShow)}</span>
                <PaginationSizeSelectorWrapper>
                    <Select
                        name="select-page-size"
                        selected={state.pagination.pageSize}
                        onChange={handlePageSizeChange}
                        size="small"
                    >
                        {Object.values(PAGINATION_SIZES).map((pageSizeOption) => (
                            <ListItem primaryText={pageSizeOption} value={pageSizeOption} key={pageSizeOption} />
                        ))}
                    </Select>
                </PaginationSizeSelectorWrapper>
                <span>
                    {t(OptimusTextKey.TablePaginationSizeItemsCount, {
                        items: totalCount.toString(),
                    })}
                </span>
            </>
        );
    } else if (totalCount && state.pagination.pageSize >= totalCount) {
        PaginationSizeElement = <>{t(OptimusTextKey.TablePaginationItemsCount, { items: totalCount.toString() })}</>;
    }

    return (
        <PaginationWrapper distribution="space-between">
            <PaginationSizeWrapper itemAlignment="center" spacing="100">
                {PaginationSizeElement}
            </PaginationSizeWrapper>
            <PaginationComponent
                selected={state.pagination.pageIndex}
                size={size}
                onClick={handlePageChange}
                alignment="end"
            />
        </PaginationWrapper>
    );
};

export default TablePagination;
